import { useState } from 'react';
import { Dumbbell, Brain, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

const pillars = [
  {
    id: 'physical',
    title: 'Physical Mastery',
    subtitle: 'Train the Feet',
    description: 'Pro-led video drills covering ball mastery, strength, speed, and recovery. Every rep is tracked, every touch counted.',
    icon: Dumbbell,
    accentColor: 'emerald',
    videoUrl: 'https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/Step-over_FutWork_Desktop_U8_U9_U10.mp4',
  },
  {
    id: 'cognitive',
    title: 'Cognitive IQ',
    subtitle: 'Sharpen the Mind',
    description: 'Interactive tactical scenarios that train the brain to read the pitch. AI-generated game situations with real-time decision scoring.',
    icon: Brain,
    accentColor: 'blue',
    videoUrl: 'https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/L-turns_Desktop_U8_U9_U10_V2.mp4',
  },
  {
    id: 'club',
    title: 'Club Synergy',
    subtitle: 'Coach-Verified Results',
    description: 'Direct training assignments from your club coach. Verified completion, team rankings, and automated progress reports.',
    icon: Users,
    accentColor: 'amber',
    videoUrl: 'https://futwork-videos.s3.us-east-1.amazonaws.com/desktop/About_Video+2.mp4',
  },
];

const colorMap: Record<string, { tab: string; activeTab: string; ring: string; iconBg: string; iconText: string }> = {
  emerald: {
    tab: 'hover:text-emerald-600',
    activeTab: 'text-emerald-600 border-emerald-500',
    ring: 'ring-emerald-500/30',
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-600',
  },
  blue: {
    tab: 'hover:text-blue-600',
    activeTab: 'text-blue-600 border-blue-500',
    ring: 'ring-blue-500/30',
    iconBg: 'bg-blue-500/10',
    iconText: 'text-blue-600',
  },
  amber: {
    tab: 'hover:text-amber-600',
    activeTab: 'text-amber-600 border-amber-500',
    ring: 'ring-amber-500/30',
    iconBg: 'bg-amber-500/10',
    iconText: 'text-amber-600',
  },
};

export function ThreePillarsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = pillars[activeIndex];
  const colors = colorMap[active.accentColor];
  const Icon = active.icon;

  return (
    <section id="features" className="py-10 md:py-16 bg-background">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6 md:mb-10">
          <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-medium mb-3">
            The FutWork System
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
            More Than a Video Library
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
            A structured training system that develops the complete athlete — body and brain.
          </p>
        </div>

        {/* Tab Bar */}
        <div className="flex justify-center border-b border-border mb-0">
          {pillars.map((pillar, i) => {
            const c = colorMap[pillar.accentColor];
            const TabIcon = pillar.icon;
            return (
              <button
                key={pillar.id}
                onClick={() => setActiveIndex(i)}
                className={cn(
                  "flex items-center gap-1.5 px-4 md:px-6 py-3 text-sm font-medium border-b-2 transition-all -mb-px",
                  i === activeIndex
                    ? c.activeTab
                    : "border-transparent text-muted-foreground " + c.tab
                )}
              >
                <TabIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{pillar.title}</span>
                <span className="sm:hidden">{pillar.title.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Active Content — side-by-side on desktop */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center pt-6 md:pt-10">
          {/* Left: Text */}
          <div key={active.id} className="animate-fade-in">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mb-4", colors.iconBg)}>
              <Icon className={cn("h-5 w-5", colors.iconText)} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-1">
              {active.title}
            </h3>
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {active.subtitle}
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              {active.description}
            </p>
          </div>

          {/* Right: Video */}
          <div key={active.id + '-video'} className="animate-fade-in">
            <div className={cn("relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl ring-1", colors.ring)}>
              <video
                key={active.videoUrl}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={active.videoUrl} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
